/** Contentful's image API serves any size and format from one stored original. */
const CONTENTFUL_IMAGES = /^(https?:)?\/\/images\.ctfassets\.net\//;

/** Width of the picture the lightbox loads: sharp on large and high-density screens. */
export const LARGE_WIDTH = 2000;

const path = (url: string) => url.split(/[?#]/)[0];

/**
 * The picture to show large. A Contentful image becomes a WebP of `width` pixels, whatever
 * size or format the page asked for; the original can be many megabytes. Contentful cannot
 * resize vectors, and WebP would freeze an animated GIF, so those and other hosts' images
 * pass through.
 */
export function largeImageUrl(url: string, width = LARGE_WIDTH): string {
    if (!CONTENTFUL_IMAGES.test(url) || /\.(svg|gif)$/i.test(path(url))) {
        return url;
    }
    return `${path(url)}?w=${width}&q=80&fm=webp`;
}

/**
 * Pictures that may be transparent (renders, device mockups, logos) need a light backing on
 * the dark lightbox: a PNG or SVG original. Photos are JPEGs.
 */
export function surfaceFor(url: string): "light" | undefined {
    return /\.(png|svg)$/i.test(path(url)) ? "light" : undefined;
}

export interface Caption {
    title: string;
    credit: string;
}

/**
 * The caption of an image editors marked up in Contentful: the figure's caption, with the
 * photographer it names in a `<cite>` given separately; the image's alt text without one.
 */
export function captionFor(image: HTMLImageElement): Caption {
    const figcaption = image.closest("figure")?.querySelector("figcaption");
    if (!figcaption) {
        return { title: image.alt.trim(), credit: "" };
    }
    const caption = figcaption.cloneNode(true) as HTMLElement;
    const cite = caption.querySelector("cite");
    const credit = cite?.textContent?.trim() ?? "";
    cite?.remove();
    const title = (caption.textContent ?? "").replace(/\s+/g, " ").trim();
    return { title: title || image.alt.trim(), credit };
}
